import { Avatar, Checkbox, Flex, Group, Space, Text, Tooltip } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconInfoCircle } from "@tabler/icons-react";
import { useCallback } from "react";
import { ArrayProperties, Campo, TRespostaField } from "../../types";
import { isCampo } from "../../util";

type Properties = {
  item: ArrayProperties;
  tipo: TTiposDeRespostas;
  disabled: boolean;
  onChange: (value: TRespostaField, item: ArrayProperties) => void;
  horizontal: boolean;
  licenciado: TLicenciado | null | undefined;
};

const DISABLED_OPACITY = 0.6;

// eslint-disable-next-line @typescript-eslint/no-unsafe-call
const CheckListComponent = ({ item, disabled, tipo, onChange, horizontal, licenciado }: Properties) => {
  const isSmMarches = useMediaQuery("(max-width: 48em)");
  const handleChangeCheckbox = useCallback(
    (value: Campo) => {
      if (!disabled) {
        onChange(value, item);
      }
    },
    [disabled, onChange, item]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>, value: Campo) => {
      if (event.key === " " || event.key === "Enter") {
        event.preventDefault();
        handleChangeCheckbox(value);
      }
    },
    [handleChangeCheckbox]
  );

  const handleFocus = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
    event.currentTarget.style.boxShadow = "0 0 0 2px #228be6";
  }, []);

  const handleBlur = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
    event.currentTarget.style.boxShadow = "none";
  }, []);

  const handleClick = useCallback(
    (value: Campo) => {
      return () => handleChangeCheckbox(value);
    },
    [handleChangeCheckbox]
  );

  const handleKeyDownWithValue = useCallback(
    (value: Campo) => {
      return (event: React.KeyboardEvent<HTMLDivElement>) => handleKeyDown(event, value);
    },
    [handleKeyDown]
  );

  const checkedField = isCampo(item.resposta) ? item.resposta.campo : null;

  return (
    <Flex justify="start" align="center" direction="column" w={"100%"} style={{ overflow: "auto" }}>
      {item.obrigatoria && (
        <>
          <Tooltip label="Está pergunta é obrigatória">
            <IconInfoCircle style={{ cursor: "pointer" }} />
          </Tooltip>
          <Space h="md" />
        </>
      )}
      {licenciado?.logo?.length && licenciado.logo.length > 0 && (
        <>
          <Group>
            {licenciado.logo.map((logoItem, index) => (
              <div key={index}>
                <Avatar src={logoItem?.path} radius="sm" size="lg" variant="default" />
                <Space h="md" />
              </div>
            ))}
          </Group>
          <Space h="md" />
        </>
      )}
      <Text fw={700} size="lg" w={isSmMarches ? "100%" : "50%"} ta="center">
        {item?.nome}
      </Text>
      <Space h="md" />
      <Text size="md" w={isSmMarches ? "100%" : "50%"} ta="center">
        {item?.descricao}
      </Text>
      <Space h="md" />
      <Flex
        align="stretch"
        justify="flex-start"
        gap="md"
        direction={horizontal ? "row" : "column"}
        pr={10}
        style={{
          height: "fit-content",
          overflow: "visible"
        }}
        w={isSmMarches ? "100%" : "50%"}
        maw={isSmMarches ? "100%" : "50%"}
      >
        {tipo?.campos &&
          Array.isArray(tipo.campos) &&
          tipo.campos.map((value: Campo) => {
            const isChecked = checkedField === value.campo;
            return (
              <div
                key={value.valor}
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-pressed={isChecked}
                onClick={handleClick(value)}
                onKeyDown={handleKeyDownWithValue(value)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: isChecked ? "#f1f7ff" : "#f9f9f9",
                  border: isChecked ? "2px solid #228be6" : "1px solid #ccc",
                  borderRadius: "12px",
                  padding: "12px 16px",
                  transition: "all 0.2s ease",
                  cursor: disabled ? "not-allowed" : "pointer",
                  opacity: disabled ? DISABLED_OPACITY : 1,
                  width: "100%",
                  outline: "none"
                }}
                onFocus={handleFocus}
                onBlur={handleBlur}
              >
                <Checkbox
                  readOnly
                  tabIndex={-1}
                  disabled={disabled}
                  checked={isChecked}
                  size="md"
                  radius="md"
                  styles={{
                    input: {
                      marginRight: "12px",
                      flexShrink: 0,
                      cursor: "pointer"
                    }
                  }}
                />
                <Text
                  style={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: disabled ? "#999" : "#333",
                    cursor: "pointer",
                    flex: 1
                  }}
                >
                  {value.campo}
                </Text>
              </div>
            );
          })}
      </Flex>
      <Space h="md" />
    </Flex>
  );
};

export default CheckListComponent;
